import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Lock } from 'lucide-react';
import { request } from '../api/client';

type ResetState = 'form' | 'loading' | 'success' | 'error';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [state, setState] = useState<ResetState>('form');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) {
      setErrorMsg('缺少重置链接参数');
      setState('error');
      return;
    }
    if (password.length < 8) {
      setErrorMsg('密码至少 8 位');
      return;
    }
    if (password !== confirm) {
      setErrorMsg('两次密码不一致');
      return;
    }
    setState('loading');
    try {
      await request<{ ok: boolean }>('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      setState('success');
    } catch (err) {
      setState('error');
      setErrorMsg(err instanceof Error ? err.message : '重置失败，链接可能已失效');
    }
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-subtle px-4">
        <div className="w-full max-w-md rounded-2xl border border-border bg-bg-surface p-8 text-center shadow-sm">
          <XCircle className="mx-auto h-12 w-12 text-danger" />
          <h1 className="mt-4 text-xl font-semibold">无效链接</h1>
          <p className="mt-2 text-sm text-text-secondary">缺少重置密码所需的参数</p>
          <Link to="/forgot-password" className="btn btn-secondary btn-md mt-6 inline-flex">
            重新发送
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-subtle px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-bg-surface p-8 shadow-sm">
        {state === 'loading' && (
          <div className="text-center">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-accent" />
            <p className="mt-4 text-sm text-text-secondary">重置中…</p>
          </div>
        )}
        {state === 'success' && (
          <div className="text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-[#22c55e]" />
            <h1 className="mt-4 text-xl font-semibold">密码已重置</h1>
            <p className="mt-2 text-sm text-text-secondary">请使用新密码登录</p>
            <Link to="/login" className="btn btn-primary btn-md mt-6 inline-flex">
              去登录
            </Link>
          </div>
        )}
        {state === 'error' && (
          <div className="text-center">
            <XCircle className="mx-auto h-12 w-12 text-danger" />
            <h1 className="mt-4 text-xl font-semibold">重置失败</h1>
            <p className="mt-2 text-sm text-text-secondary">{errorMsg}</p>
            <Link to="/forgot-password" className="btn btn-secondary btn-md mt-6 inline-flex">
              重新发送
            </Link>
          </div>
        )}
        {state === 'form' && (
          <>
            <h1 className="text-xl font-semibold">重置密码</h1>
            <p className="mt-1 text-sm text-text-secondary">请输入新密码</p>
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="new-pwd">
                  新密码
                </label>
                <input
                  id="new-pwd"
                  type="password"
                  className="input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoFocus
                  placeholder="至少 8 位，需含字母和数字"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="confirm-pwd">
                  确认新密码
                </label>
                <input
                  id="confirm-pwd"
                  type="password"
                  className="input"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  placeholder="再次输入新密码"
                />
              </div>
              {errorMsg && <p className="text-sm text-danger">{errorMsg}</p>}
              <button type="submit" className="btn btn-primary btn-md w-full">
                <Lock className="h-4 w-4" /> 重置密码
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}